/**
 * themeSwitcher.helper.js — pure logic + constants extracted from
 * ThemeSwitcher.jsx so HomeMenu can render the theme picker inline
 * without instantiating the (now-removed) floating ThemeSwitcher
 * button. The legacy applyTheme/loadTheme/THEMES exports here are
 * the source of truth.
 */

export const CUSTOM_THEME_ID = 'custom';

export const CUSTOM_THEME_FIELDS = [
  { key: 'bezel', label: 'Bezel', cssVar: '--bezel', fallback: '#1a1810' },
  { key: 'bezelEdge', label: 'Bezel edge', cssVar: '--bezel-edge', fallback: '#2a2517' },
  { key: 'screenBg', label: 'Desktop base', cssVar: '--screen-bg', fallback: '#0a0a08' },
  { key: 'screenBg2', label: 'Panel base', cssVar: '--screen-bg-2', fallback: '#11110d' },
  { key: 'windowBg', label: 'Window fill', cssVar: '--window-bg', fallback: '#0e0e0a' },
  { key: 'windowEdge', label: 'Window edge', cssVar: '--window-edge', fallback: '#2b2918' },
  { key: 'text', label: 'Primary text', cssVar: '--t-green', fallback: '#4ade80' },
  { key: 'textDim', label: 'Muted text', cssVar: '--t-green-dim', fallback: '#2f9d5d' },
  { key: 'cyan', label: 'Entity accent', cssVar: '--t-cyan', fallback: '#22d3ee' },
  { key: 'gold', label: 'Gold accent', cssVar: '--gold', fallback: '#d4a24e' },
  { key: 'fissal', label: 'Main glow', cssVar: '--fissal', fallback: '#00ff88' },
  { key: 'focusAccent', label: 'Focus accent', cssVar: '--focus-accent', fallback: '#00ff88' },
];

export const THEMES = [
  { id: 'fissal',   label: 'Fissal',   swatch: '#00ff88', desc: 'Alfiq automaton emerald' },
  { id: 'amber',    label: 'Amber',    swatch: '#ffb938', desc: 'Warm CRT earth' },
  { id: 'cyan',     label: 'Ice',      swatch: '#22d3ee', desc: 'Clinical lab' },
  { id: 'plasma',   label: 'Plasma',   swatch: '#ff4d8a', desc: 'Arcade neon' },
  { id: 'void',     label: 'Void',     swatch: '#a855f7', desc: 'Midnight Senche' },
  { id: 'phosphor', label: 'Phosphor', swatch: '#a3ff00', desc: 'Brutalist P1' },
  // ESO Lore Themes
  { id: 'khajiit', label: 'Khajiit', swatch: '#e2b97f', desc: 'Desert dusk & moon-sugar' },
  { id: 'dunmer', label: 'Dunmer', swatch: '#a13c3c', desc: 'Vvardenfell ash & bonemold' },
  { id: 'orsimer', label: 'Orsimer', swatch: '#4e7c3a', desc: 'Forge-iron & tribal green' },
  { id: 'necrom', label: 'Necrom', swatch: '#7e6bb7', desc: 'Apocrypha violet & bone' },
  { id: 'blackwood', label: 'Blackwood', swatch: '#2e6e5c', desc: 'Swamp moss & marsh teal' },
];

const KEY = 'redfur:v2:theme';
const KEY_GLOW = 'redfur:v2:glow';        // percent (0-100); legacy values 'off'|'low'|'med'|'high' migrated
const KEY_NEON = 'redfur:v2:neon';        // percent (0-100)
const KEY_WMBORDER = 'redfur:v2:wmBorder'; // px (1-3)
const KEY_DASH_IMAGE = 'redfur:v2:dashboardImage';
const KEY_DASH_IMAGE_FIT = 'redfur:v2:dashboardImageFit';
const KEY_DASH_IMAGE_POS_X = 'redfur:v2:dashboardImagePosX';
const KEY_DASH_IMAGE_POS_Y = 'redfur:v2:dashboardImagePosY';
const KEY_COMPACT = 'redfur:v2:compactWindows';
const KEY_FONT_SCALE = 'redfur:v2:fontScale'; // percent (85-130)
const KEY_CUSTOM_THEME = 'redfur:v2:customTheme';
const KEY_THEME_OVERRIDES = 'redfur:v2:themeOverrides';

export const DEFAULT_GLOW_PCT = 40;
export const DEFAULT_NEON_PCT = 100;
export const DEFAULT_WM_BORDER_PX = 1;
export const DEFAULT_FONT_SCALE_PCT = 100;
export const DASH_IMAGE_UPLOAD_MAX_BYTES = 1_500_000;
export const DASH_IMAGE_REMOTE_MAX_BYTES = 8_000_000;
export const DASH_IMAGE_LOCAL_STORAGE_MAX_CHARS = 2_200_000;
export const DASH_IMAGE_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif'];
export const DASH_IMAGE_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif'];
export const DASH_IMAGE_FITS = [
  { id: 'cover', label: 'Crop to fill' },
  { id: 'contain', label: 'Fit whole image' },
  { id: 'stretch', label: 'Stretch' },
];

const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value));
const CUSTOM_THEME_DEFAULTS = Object.freeze(Object.fromEntries(
  CUSTOM_THEME_FIELDS.map(field => [field.key, field.fallback]),
));
const CUSTOM_THEME_STYLE_VARS = [
  ...CUSTOM_THEME_FIELDS.map(field => field.cssVar),
  '--fissal-haze',
  '--theme-gradient-header',
  '--theme-gradient-border',
  '--theme-pattern',
  '--theme-pattern-opacity',
  '--theme-pattern-blend',
  '--theme-mark',
];

function normalizeHexColor(value, fallback) {
  const clean = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(clean)) return clean.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(clean)) {
    const [, red, green, blue] = clean.toLowerCase();
    return `#${red}${red}${green}${green}${blue}${blue}`;
  }
  return fallback;
}

function normalizeColorValue(value, fallback) {
  const clean = String(value || '').trim();
  const hex = normalizeHexColor(clean, '');
  if (hex) return hex;
  const rgb = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(clean);
  if (rgb) {
    return '#' + rgb.slice(1, 4).map(channel => clamp(parseInt(channel, 10) || 0, 0, 255).toString(16).padStart(2, '0')).join('');
  }
  return fallback;
}

function hexToRgba(hex, alpha) {
  const clean = normalizeHexColor(hex, '#00ff88').slice(1);
  const red = parseInt(clean.slice(0, 2), 16);
  const green = parseInt(clean.slice(2, 4), 16);
  const blue = parseInt(clean.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function normalizeCustomTheme(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const next = {};
  for (const field of CUSTOM_THEME_FIELDS) {
    next[field.key] = normalizeHexColor(source[field.key], field.fallback);
  }
  return next;
}

function readThemeOverrides() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY_THEME_OVERRIDES) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

function writeThemeOverrides(overrides) {
  try { localStorage.setItem(KEY_THEME_OVERRIDES, JSON.stringify(overrides || {})); } catch { /* ignore */ }
}

function computedThemeDefaults() {
  const defaults = {};
  if (typeof document === 'undefined') return normalizeCustomTheme(CUSTOM_THEME_DEFAULTS);
  const style = getComputedStyle(document.body);
  for (const field of CUSTOM_THEME_FIELDS) {
    defaults[field.key] = normalizeColorValue(style.getPropertyValue(field.cssVar), field.fallback);
  }
  return normalizeCustomTheme(defaults);
}

function clearCustomThemeVars() {
  if (typeof document === 'undefined') return;
  for (const cssVar of CUSTOM_THEME_STYLE_VARS) {
    document.body.style.removeProperty(cssVar);
  }
}

function applyCustomThemeVars(values, options = {}) {
  if (typeof document === 'undefined') return;
  const customTheme = normalizeCustomTheme(values);
  for (const field of CUSTOM_THEME_FIELDS) {
    document.body.style.setProperty(field.cssVar, customTheme[field.key]);
  }
  document.body.style.setProperty('--fissal-haze', hexToRgba(customTheme.fissal, 0.28));
  document.body.style.setProperty('--theme-gradient-header', `linear-gradient(180deg, ${customTheme.bezel} 0%, ${customTheme.bezelEdge} 52%, ${customTheme.screenBg} 100%)`);
  document.body.style.setProperty('--theme-gradient-border', `linear-gradient(180deg, ${customTheme.focusAccent}, ${customTheme.windowEdge} 52%, ${customTheme.bezel})`);
  if (options.neutralizePattern) {
    document.body.style.setProperty('--theme-pattern', 'none');
    document.body.style.setProperty('--theme-pattern-opacity', '0');
    document.body.style.setProperty('--theme-pattern-blend', 'normal');
    document.body.style.setProperty('--theme-mark', "'*'");
  }
}

export function storedThemeOverride(themeId) {
  const id = THEMES.some(theme => theme.id === themeId) ? themeId : 'fissal';
  const raw = readThemeOverrides()[id];
  return raw && typeof raw === 'object' ? normalizeCustomTheme(raw) : null;
}

export function currentThemeOverride(themeId = currentTheme()) {
  return storedThemeOverride(themeId) || computedThemeDefaults();
}

export function applyThemeOverride(themeId, patch) {
  const id = THEMES.some(theme => theme.id === themeId) ? themeId : currentTheme();
  const overrides = readThemeOverrides();
  const next = normalizeCustomTheme({ ...currentThemeOverride(id), ...(patch || {}) });
  overrides[id] = next;
  writeThemeOverrides(overrides);
  if (currentTheme() === id) applyTheme(id);
  return next;
}

export function previewThemeOverride(themeId, draftValues) {
  const id = THEMES.some(theme => theme.id === themeId) ? themeId : currentTheme();
  if (typeof document === 'undefined' || currentTheme() !== id) return false;
  applyCustomThemeVars(draftValues || currentThemeOverride(id));
  return true;
}

export function clearThemeOverridePreview(themeId = currentTheme()) {
  const id = THEMES.some(theme => theme.id === themeId) ? themeId : currentTheme();
  if (typeof document === 'undefined' || currentTheme() !== id) return false;
  applyTheme(id);
  return true;
}

export function resetThemeOverride(themeId = currentTheme()) {
  const id = THEMES.some(theme => theme.id === themeId) ? themeId : currentTheme();
  const overrides = readThemeOverrides();
  delete overrides[id];
  writeThemeOverrides(overrides);
  if (currentTheme() === id) applyTheme(id);
  return currentThemeOverride(id);
}

export function currentCustomTheme() {
  try {
    const raw = localStorage.getItem(KEY_CUSTOM_THEME);
    return normalizeCustomTheme(raw ? JSON.parse(raw) : CUSTOM_THEME_DEFAULTS);
  } catch {
    return normalizeCustomTheme(CUSTOM_THEME_DEFAULTS);
  }
}

export function applyCustomTheme(patch) {
  const next = normalizeCustomTheme({ ...currentCustomTheme(), ...(patch || {}) });
  try { localStorage.setItem(KEY_CUSTOM_THEME, JSON.stringify(next)); } catch { /* ignore */ }
  if (currentTheme() === CUSTOM_THEME_ID) applyCustomThemeVars(next, { neutralizePattern: true });
  return next;
}

export function resetCustomTheme() {
  try { localStorage.removeItem(KEY_CUSTOM_THEME); } catch { /* ignore */ }
  const next = normalizeCustomTheme(CUSTOM_THEME_DEFAULTS);
  if (currentTheme() === CUSTOM_THEME_ID) applyCustomThemeVars(next, { neutralizePattern: true });
  return next;
}

export function applyTheme(id) {
  const themeId = THEMES.find(theme => theme.id === id) ? id : 'fissal';
  if (typeof document !== 'undefined') {
    document.body.setAttribute('data-theme', themeId);
    clearCustomThemeVars();
    const override = storedThemeOverride(themeId);
    if (themeId === CUSTOM_THEME_ID) applyCustomThemeVars(currentCustomTheme(), { neutralizePattern: true });
    else if (override) applyCustomThemeVars(override);
  }
  try { localStorage.setItem(KEY, themeId); } catch { /* private mode / quota */ }
}

/**
 * CRT glow intensity as a percentage (0–100). Drives `--glow-mul` on
 * <body>; tokens.css derives `--fissal-glow`/`--glow-sm`/`--glow-md`
 * from this multiplier. 0% leaves a hairline focus outline; 100%
 * matches the previous "high" preset. Issue #130.
 */
export function applyGlowPercent(pct) {
  const value = clamp(Number(pct) || 0, 0, 100);
  if (typeof document !== 'undefined') {
    document.body.style.setProperty('--glow-mul', String(value / 100));
    document.body.removeAttribute('data-glow'); // clean legacy attr
  }
  try { localStorage.setItem(KEY_GLOW, String(value)); } catch { /* ignore */ }
  return value;
}

export function currentGlowPercent() {
  try {
    const raw = localStorage.getItem(KEY_GLOW);
    if (raw == null) return DEFAULT_GLOW_PCT;
    // Migrate legacy discrete values.
    if (raw === 'off')  return 0;
    if (raw === 'low')  return 20;
    if (raw === 'med')  return 40;
    if (raw === 'high') return 80;
    const value = parseInt(raw, 10);
    return Number.isFinite(value) ? clamp(value, 0, 100) : DEFAULT_GLOW_PCT;
  } catch { return DEFAULT_GLOW_PCT; }
}

/**
 * Neon text brightness as a percentage (0–100). Drives `--neon-mul`
 * on <body>; CSS rules that emit a phosphor text-shadow multiply
 * their alpha by this value. Issue #131.
 */
export function applyNeonPercent(pct) {
  const value = clamp(Number(pct) || 0, 0, 100);
  if (typeof document !== 'undefined') {
    document.body.style.setProperty('--neon-mul', String(value / 100));
  }
  try { localStorage.setItem(KEY_NEON, String(value)); } catch { /* ignore */ }
  return value;
}

export function currentNeonPercent() {
  try {
    const value = parseInt(localStorage.getItem(KEY_NEON) || '', 10);
    return Number.isFinite(value) ? clamp(value, 0, 100) : DEFAULT_NEON_PCT;
  } catch { return DEFAULT_NEON_PCT; }
}

/**
 * Window border thickness in px (1–3). Drives `--wm-border-w` on
 * <body>; .v2-window border-width references the token. Issue #132.
 */
export function applyWmBorderWidth(px) {
  const value = clamp(parseInt(px, 10) || DEFAULT_WM_BORDER_PX, 1, 3);
  if (typeof document !== 'undefined') {
    document.body.style.setProperty('--wm-border-w', `${value}px`);
  }
  try { localStorage.setItem(KEY_WMBORDER, String(value)); } catch { /* ignore */ }
  return value;
}

export function currentWmBorderWidth() {
  try {
    const value = parseInt(localStorage.getItem(KEY_WMBORDER) || '', 10);
    return Number.isFinite(value) ? clamp(value, 1, 3) : DEFAULT_WM_BORDER_PX;
  } catch { return DEFAULT_WM_BORDER_PX; }
}

/**
 * Global text scale for v2 chrome/widgets. Drives --ui-font-scale so users can
 * lift dense widgets without changing browser zoom or breaking fixed boards.
 */
export function applyFontScalePercent(pct) {
  const value = clamp(Number(pct) || DEFAULT_FONT_SCALE_PCT, 85, 130);
  const multiplier = value / 100;
  const basePx = { '3xs': 9, '2xs': 10, xs: 13, sm: 14, md: 16, lg: 19, xl: 24 };
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.style.setProperty('--ui-font-scale', String(multiplier));
    for (const [key, base] of Object.entries(basePx)) {
      root.style.setProperty(`--ui-font-delta-${key}`, `${((multiplier - 1) * base).toFixed(2)}px`);
    }
  }
  try { localStorage.setItem(KEY_FONT_SCALE, String(value)); } catch { /* ignore */ }
  return value;
}

export function currentFontScalePercent() {
  try {
    const value = parseInt(localStorage.getItem(KEY_FONT_SCALE) || '', 10);
    return Number.isFinite(value) ? clamp(value, 85, 130) : DEFAULT_FONT_SCALE_PCT;
  } catch { return DEFAULT_FONT_SCALE_PCT; }
}

export function isAllowedDashboardImageFileType(type) {
  return DASH_IMAGE_ALLOWED_TYPES.includes(String(type || '').toLowerCase());
}

export function dashboardImageExtensionFromUrl(raw) {
  try {
    const parsed = new URL(String(raw || '').trim());
    const match = /\.([a-z0-9]+)$/i.exec(parsed.pathname || '');
    return match ? match[1].toLowerCase() : '';
  } catch {
    return '';
  }
}

export function dashboardImageUrlHasAllowedExtension(raw) {
  const extension = dashboardImageExtensionFromUrl(raw);
  return !!extension && DASH_IMAGE_ALLOWED_EXTENSIONS.includes(extension);
}

function isAllowedDashboardDataImage(clean) {
  const match = /^data:(image\/[a-z0-9.+-]+);base64,[a-z0-9+/=]+$/i.exec(clean);
  return !!match && isAllowedDashboardImageFileType(match[1]) && clean.length <= DASH_IMAGE_LOCAL_STORAGE_MAX_CHARS;
}

function isAllowedDashboardRemoteImage(clean) {
  try {
    const parsed = new URL(clean);
    if (!/^https?:$/.test(parsed.protocol)) return false;
    const extension = dashboardImageExtensionFromUrl(clean);
    return extension !== 'svg';
  } catch {
    return false;
  }
}

function normalizeDashboardImageFit(value) {
  return DASH_IMAGE_FITS.some(fit => fit.id === value) ? value : 'cover';
}

function normalizeDashboardImagePosition(value) {
  const next = parseInt(value, 10);
  return Number.isFinite(next) ? clamp(next, 0, 100) : 50;
}

function normalizeDashboardImageControls(controls) {
  const source = controls && typeof controls === 'object' ? controls : {};
  const currentPosition = currentDashboardImagePosition();
  return {
    fit: normalizeDashboardImageFit(source.fit ?? currentDashboardImageFit()),
    x: normalizeDashboardImagePosition(source.x ?? currentPosition.x),
    y: normalizeDashboardImagePosition(source.y ?? currentPosition.y),
  };
}

function dashboardImageCssSize(fit) {
  if (fit === 'contain') return 'contain';
  if (fit === 'stretch') return '100% 100%';
  return 'cover';
}

function sanitizeDashboardImage(raw) {
  if (!raw) return 'none';
  const clean = String(raw).trim();
  if (!clean) return 'none';
  if (!isAllowedDashboardDataImage(clean) && !isAllowedDashboardRemoteImage(clean)) return 'none';
  return `url("${clean.replace(/["\\\n\r\f]/g, char => encodeURIComponent(char))}")`;
}

export function applyDashboardImage(raw, controls) {
  const rawValue = raw ? String(raw).trim() : '';
  const value = sanitizeDashboardImage(rawValue);
  const hasImage = value !== 'none';
  const imageControls = normalizeDashboardImageControls(controls);
  if (typeof document !== 'undefined') {
    document.body.style.setProperty('--dashboard-image', value);
    document.body.style.setProperty('--dashboard-image-size', hasImage ? dashboardImageCssSize(imageControls.fit) : 'auto');
    document.body.style.setProperty('--dashboard-image-position', hasImage ? `${imageControls.x}% ${imageControls.y}%` : '0 0');
    document.body.style.setProperty('--dashboard-image-repeat', hasImage ? 'no-repeat' : 'repeat');
    // Toggle a class so CSS can override the theme pattern's blend
    // mode + opacity when a real image is set — otherwise the user's
    // photo gets multiplied/screened against the theme texture and
    // ends up looking distorted instead of clean.
    document.body.classList.toggle('has-dash-image', hasImage);
  }
  try {
    if (hasImage) localStorage.setItem(KEY_DASH_IMAGE, rawValue);
    else localStorage.removeItem(KEY_DASH_IMAGE);
    localStorage.setItem(KEY_DASH_IMAGE_FIT, imageControls.fit);
    localStorage.setItem(KEY_DASH_IMAGE_POS_X, String(imageControls.x));
    localStorage.setItem(KEY_DASH_IMAGE_POS_Y, String(imageControls.y));
  } catch { /* ignore */ }
  return hasImage ? rawValue : '';
}

export function currentDashboardImage() {
  try {
    return localStorage.getItem(KEY_DASH_IMAGE) || '';
  } catch {
    return '';
  }
}

export function currentDashboardImageFit() {
  try { return normalizeDashboardImageFit(localStorage.getItem(KEY_DASH_IMAGE_FIT)); }
  catch { return 'cover'; }
}

export function currentDashboardImagePosition() {
  try {
    return {
      x: normalizeDashboardImagePosition(localStorage.getItem(KEY_DASH_IMAGE_POS_X)),
      y: normalizeDashboardImagePosition(localStorage.getItem(KEY_DASH_IMAGE_POS_Y)),
    };
  } catch {
    return { x: 50, y: 50 };
  }
}

export function applyDashboardImageControls(controls) {
  const next = normalizeDashboardImageControls(controls);
  applyDashboardImage(currentDashboardImage(), next);
  return next;
}

export function applyCompactWindows(enabled) {
  const live = !!enabled;
  if (typeof document !== 'undefined') {
    document.body.classList.toggle('compact-windows', live);
  }
  try {
    localStorage.setItem(KEY_COMPACT, String(live));
  } catch { /* ignore */ }
  return live;
}

export function currentCompactWindows() {
  try {
    const raw = localStorage.getItem(KEY_COMPACT);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

export function loadTheme() {
  try {
    const themeId = localStorage.getItem(KEY);
    applyTheme(themeId || 'fissal');
  } catch {
    applyTheme('fissal');
  }
  applyGlowPercent(currentGlowPercent());
  applyNeonPercent(currentNeonPercent());
  applyWmBorderWidth(currentWmBorderWidth());
  applyFontScalePercent(currentFontScalePercent());
  applyDashboardImage(currentDashboardImage());
  applyCompactWindows(currentCompactWindows());
}

export function currentTheme() {
  try {
    const themeId = localStorage.getItem(KEY) || 'fissal';
    return THEMES.some(theme => theme.id === themeId) ? themeId : 'fissal';
  }
  catch { return 'fissal'; }
}
